import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  modo: 'new' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const TIPOS_APOYO = [
  'Despensa alimentaria',
  'Apoyo económico',
  'Material de construcción',
  'Útiles escolares',
  'Medicamentos',
  'Ropa y calzado',
  'Silla de ruedas / equipo médico',
  'Fertilizantes / insumos agrícolas',
  'Otro',
];

const FormularioApoyo: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    programaId: '',
    beneficiarioId: '',
    domicilio: '',
    telefono: '',
    tipoApoyo: '',
    cantidadEntregada: '',
    fechaEntrega: '',
    responsableEntrega: '',
  });
  const [files, setFiles] = useState<any>({ docFirmado: null, evidenciasFotos: [] });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [programas, setProgramas] = useState<any[]>([]);
  const [beneficiarios, setBeneficiarios] = useState<any[]>([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);

  useEffect(() => {
    if (modo === 'new') {
      setCargandoCatalogos(true);
      Promise.all([
        axiosInstance.get('/desarrollo-social/programas').catch(() => ({ data: [] })),
        axiosInstance.get('/desarrollo-social/beneficiarios').catch(() => ({ data: [] })),
      ]).then(([pRes, bRes]) => {
        setProgramas(pRes.data || []);
        setBeneficiarios(bRes.data || []);
      }).finally(() => setCargandoCatalogos(false));
    }
  }, [modo]);

  useEffect(() => {
    if (data) {
      setForm({
        programaId: data.programaId || '',
        beneficiarioId: data.beneficiarioId || '',
        domicilio: data.domicilio || '',
        telefono: data.telefono || '',
        tipoApoyo: data.tipoApoyo || '',
        cantidadEntregada: data.cantidadEntregada || '',
        fechaEntrega: data.fechaEntrega ? data.fechaEntrega.split('T')[0] : '',
        responsableEntrega: data.responsableEntrega || '',
      });
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleBeneficiarioChange = (id: string) => {
    handleChange('beneficiarioId', id);
    if (!id) return;
    const ben = beneficiarios.find((b) => String(b.id) === id);
    if (ben) {
      setForm((prev: any) => ({
        ...prev,
        beneficiarioId: id,
        domicilio: ben.domicilioCompleto || prev.domicilio,
        telefono: ben.telefono || prev.telefono,
      }));
    }
  };

  const handleFile = (field: string, file: File | null) => {
    setFiles((prev: any) => ({ ...prev, [field]: file }));
  };

  const handleFotos = (fileList: FileList | null) => {
    const arr = fileList ? Array.from(fileList).slice(0, 3) : [];
    setFiles((prev: any) => ({ ...prev, evidenciasFotos: arr }));
  };

  const handleSubmit = async () => {
    if (!form.programaId || !form.beneficiarioId || !form.tipoApoyo || !form.fechaEntrega) {
      setError('Programa, beneficiario, tipo de apoyo y fecha son obligatorios.');
      return;
    }
    setError('');
    setGuardando(true);
    try {
      if (modo === 'new') {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, String(v ?? '')));
        if (files.docFirmado) fd.append('docFirmado', files.docFirmado);
        if (files.evidenciasFotos?.length) {
          files.evidenciasFotos.forEach((f: File) => fd.append('evidenciasFotos', f));
        }
        await axiosInstance.post('/desarrollo-social/apoyos', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onGuardado && onGuardado();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No fue posible registrar el apoyo.');
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  const programaSeleccionado = programas.find((p) => String(p.id) === String(form.programaId));
  const beneficiarioSeleccionado = beneficiarios.find((b) => String(b.id) === String(form.beneficiarioId));

  return (
    <Box>
      {cargandoCatalogos ? (
        <Box textAlign="center" py={8}>
          <Spinner color="orange.500" />
          <Text mt={2} color="gray.500" fontSize="sm">Cargando catálogos...</Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {/* Programa */}
          <FormControl isRequired>
            <FormLabel>Programa social</FormLabel>
            {disabled ? (
              <Input value={data?.programa?.nombre || form.programaId} isDisabled />
            ) : (
              <>
                <Select
                  placeholder="Selecciona un programa"
                  value={form.programaId}
                  onChange={(e) => handleChange('programaId', e.target.value)}
                >
                  {programas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </Select>
                {programaSeleccionado?.descripcion && (
                  <FormHelperText>{programaSeleccionado.descripcion}</FormHelperText>
                )}
                {programas.length === 0 && (
                  <FormHelperText color="orange.500">No hay programas registrados.</FormHelperText>
                )}
              </>
            )}
          </FormControl>

          {/* Beneficiario */}
          <FormControl isRequired>
            <FormLabel>Beneficiario</FormLabel>
            {disabled ? (
              <Input value={data?.beneficiario?.nombreCompleto || form.beneficiarioId} isDisabled />
            ) : (
              <>
                <Select
                  placeholder="Selecciona un beneficiario"
                  value={form.beneficiarioId}
                  onChange={(e) => handleBeneficiarioChange(e.target.value)}
                >
                  {beneficiarios.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.folio} — {b.nombreCompleto}
                    </option>
                  ))}
                </Select>
                {beneficiarioSeleccionado?.curp && (
                  <FormHelperText>CURP: {beneficiarioSeleccionado.curp}</FormHelperText>
                )}
                {beneficiarios.length === 0 && (
                  <FormHelperText color="orange.500">No hay beneficiarios registrados.</FormHelperText>
                )}
              </>
            )}
          </FormControl>

          {/* Domicilio — se auto-rellena al seleccionar beneficiario */}
          <FormControl>
            <FormLabel>Domicilio</FormLabel>
            <Textarea
              value={form.domicilio}
              onChange={(e) => handleChange('domicilio', e.target.value)}
              isDisabled={disabled}
              rows={2}
            />
          </FormControl>

          {/* Teléfono */}
          <FormControl>
            <FormLabel>Teléfono</FormLabel>
            <Input
              value={form.telefono}
              onChange={(e) => handleChange('telefono', e.target.value)}
              isDisabled={disabled}
            />
          </FormControl>

          {/* Tipo de apoyo */}
          <FormControl isRequired>
            <FormLabel>Tipo de apoyo</FormLabel>
            {disabled ? (
              <Input value={form.tipoApoyo} isDisabled />
            ) : (
              <Select
                placeholder="Selecciona tipo"
                value={form.tipoApoyo}
                onChange={(e) => handleChange('tipoApoyo', e.target.value)}
              >
                {TIPOS_APOYO.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            )}
          </FormControl>

          {/* Cantidad */}
          <FormControl>
            <FormLabel>Cantidad entregada</FormLabel>
            <Input
              value={form.cantidadEntregada}
              onChange={(e) => handleChange('cantidadEntregada', e.target.value)}
              isDisabled={disabled}
              placeholder="Ej. 1 despensa, $500, 5 bultos"
            />
          </FormControl>

          {/* Fecha */}
          <FormControl isRequired>
            <FormLabel>Fecha de entrega</FormLabel>
            <Input
              type="date"
              value={form.fechaEntrega}
              onChange={(e) => handleChange('fechaEntrega', e.target.value)}
              isDisabled={disabled}
            />
          </FormControl>

          {/* Responsable */}
          <FormControl>
            <FormLabel>Responsable de entrega</FormLabel>
            <Input
              value={form.responsableEntrega}
              onChange={(e) => handleChange('responsableEntrega', e.target.value)}
              isDisabled={disabled}
              placeholder="Nombre del funcionario"
            />
          </FormControl>
        </SimpleGrid>
      )}

      {modo === 'new' && !cargandoCatalogos && (
        <Box mt={6}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Documento firmado</FormLabel>
              <Input type="file" accept=".pdf,image/*" onChange={(e) => handleFile('docFirmado', e.target.files?.[0] || null)} p={1} />
            </FormControl>
            <FormControl>
              <FormLabel>Evidencias fotográficas (máx. 3)</FormLabel>
              <Input type="file" accept="image/*" multiple onChange={(e) => handleFotos(e.target.files)} p={1} />
            </FormControl>
          </SimpleGrid>
        </Box>
      )}

      {error && (
        <Box mt={4} borderWidth="1px" borderColor="red.200" bg="red.50" borderRadius="md" p={3}>
          <Text color="red.600" fontSize="sm">{error}</Text>
        </Box>
      )}

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">
            Guardar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioApoyo;
